import React, { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import DomainCardButton from '../../domain-cards/ui/DomainCardButton';

const SECTIONS = [
  { key: 'ancestry', label: 'Ancestry', proposalKey: 'ancestry' },
  { key: 'community', label: 'Community', proposalKey: 'community' },
  { key: 'class', label: 'Class & Subclass', proposalKey: 'class' },
  { key: 'traits', label: 'Traits', proposalKey: 'traits' },
  { key: 'homebrewMechanics', label: 'Homebrew', proposalKey: 'homebrewMechanics' },
  { key: 'domainCards', label: 'Domain Cards', proposalKey: 'domainCards' },
  { key: 'equipment', label: 'Equipment', proposalKey: 'equipment' },
  { key: 'experiences', label: 'Background', proposalKey: 'experiences' },
];

const STATUS = {
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  EDITING: 'EDITING',
  REVISING: 'REVISING',
};

function formatSectionValue(proposal, section) {
  const data = proposal[section.proposalKey];
  if (!data) return 'Not set';

  switch (section.key) {
    case 'ancestry':
      if (data.type === 'mixed') return `${data.primary} + ${data.secondary}`;
      return data.primary || data.selected || JSON.stringify(data);

    case 'community':
      return data.selected || JSON.stringify(data);

    case 'class': {
      const cls = proposal.class?.selected || '';
      const sub = proposal.subclass?.selected || '';
      return sub ? `${cls} / ${sub}` : cls;
    }

    case 'traits':
      if (typeof data === 'object' && !Array.isArray(data)) {
        return Object.entries(data)
          .map(([k, v]) => `${k[0].toUpperCase() + k.slice(1, 3)}: ${v >= 0 ? '+' : ''}${v}`)
          .join('  ');
      }
      return JSON.stringify(data);

    case 'homebrewMechanics':
      if (Array.isArray(data)) return `${data.length} mechanic(s)`;
      return 'None';

    case 'domainCards': {
      const loadout = data.loadout || data;
      if (Array.isArray(loadout)) return `${loadout.length} card(s)`;
      return 'Not set';
    }

    case 'equipment':
      if (Array.isArray(data)) return `${data.length} item(s)`;
      if (typeof data === 'object') {
        const parts = [];
        if (data.weapon1) parts.push(data.weapon1);
        if (data.weapon2) parts.push(data.weapon2);
        if (data.armor) parts.push(data.armor);
        return parts.join(', ') || 'Not set';
      }
      return String(data);

    case 'experiences':
      if (typeof data === 'object' && data.background) return data.background;
      if (Array.isArray(data)) return data.join(', ');
      return String(data);

    default:
      return JSON.stringify(data);
  }
}

function BrewmasterProposalReview({ proposal, parsedCharacter, userId, onProposalUpdate, onConfirm, onSectionChange }) {
  const [statuses, setStatuses] = useState(() => {
    const initial = {};
    SECTIONS.forEach((s) => { initial[s.key] = STATUS.PENDING; });
    return initial;
  });
  const [editingKey, setEditingKey] = useState(null);
  const [editText, setEditText] = useState('');

  const allApproved = SECTIONS.every((s) => statuses[s.key] === STATUS.APPROVED);
  const anyRevising = SECTIONS.some((s) => statuses[s.key] === STATUS.REVISING);

  const handleApprove = useCallback((key) => {
    setStatuses((prev) => ({ ...prev, [key]: STATUS.APPROVED }));
    if (editingKey === key) {
      setEditingKey(null);
      setEditText('');
    }
  }, [editingKey]);

  const handleApproveAll = useCallback(() => {
    setStatuses((prev) => {
      const next = { ...prev };
      SECTIONS.forEach((s) => { next[s.key] = STATUS.APPROVED; });
      return next;
    });
    setEditingKey(null);
    setEditText('');
  }, []);

  const handleStartEdit = useCallback((key) => {
    setEditingKey(key);
    setEditText('');
  }, []);

  const handleRevise = useCallback(async () => {
    if (!editingKey || !editText.trim()) return;

    const key = editingKey;
    const text = editText.trim();
    setStatuses((prev) => ({ ...prev, [key]: STATUS.REVISING }));
    setEditingKey(null);
    setEditText('');

    try {
      if (onSectionChange) {
        await onSectionChange(key, text);
      }
      setStatuses((prev) => ({ ...prev, [key]: STATUS.PENDING }));
      toast.success(`${SECTIONS.find((s) => s.key === key)?.label} updated`);
    } catch (err) {
      setStatuses((prev) => ({ ...prev, [key]: STATUS.EDITING }));
      setEditingKey(key);
      toast.error('Revision failed. Try again.');
    }
  }, [editingKey, editText, onSectionChange]);

  const targetLevel = proposal.targetLevel?.dhLevel;

  return (
    <div className="mx-4 my-2 rounded-lg border border-gray-700 bg-gray-800/40">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
        <h4 className="text-sm font-semibold text-gray-300">Conversion Proposal</h4>
        <div className="flex items-center gap-2">
          {!allApproved && (
            <DomainCardButton
              onClick={handleApproveAll}
              variant="activeSuccess"
              className="px-2 py-0.5"
            >
              Approve all
            </DomainCardButton>
          )}
        </div>
      </div>

      {/* Table */}
      <table className="w-full text-sm">
        <tbody>
          {SECTIONS.map((section) => {
            const status = statuses[section.key];
            const isApproved = status === STATUS.APPROVED;
            const isRevising = status === STATUS.REVISING;

            return (
              <tr
                key={section.key}
                className={`border-b border-gray-700/50 last:border-b-0 ${
                  isApproved ? 'bg-green-900/10' : ''
                }`}
              >
                <td className="px-4 py-2 text-gray-400 font-medium whitespace-nowrap w-[120px]">
                  {section.label}
                </td>
                <td className="px-2 py-2 text-amber-300 truncate max-w-0">
                  {formatSectionValue(proposal, section)}
                </td>
                <td className="px-2 py-2 whitespace-nowrap text-right w-[140px]">
                  {isRevising ? (
                    <span className="flex items-center justify-end gap-1 text-xs text-purple-400">
                      <span className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-500" />
                      Revising...
                    </span>
                  ) : (
                    <div className="flex items-center justify-end gap-1">
                      <DomainCardButton
                        onClick={() => handleApprove(section.key)}
                        disabled={isApproved}
                        isActive={isApproved}
                        variant="activeSuccess"
                        activeVariant="activeSuccess"
                        className="px-2 py-0.5"
                      >
                        {isApproved ? '✓' : 'OK'}
                      </DomainCardButton>
                      {!isApproved && (
                        <DomainCardButton
                          onClick={() => handleStartEdit(section.key)}
                          variant="warning"
                          className="px-2 py-0.5"
                        >
                          Change
                        </DomainCardButton>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Inline edit area (shows below table when editing) */}
      {editingKey && (
        <div className="px-4 py-2 border-t border-gray-700 flex items-center gap-2">
          <span className="text-xs text-gray-500 whitespace-nowrap">
            {SECTIONS.find((s) => s.key === editingKey)?.label}:
          </span>
          <input
            type="text"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleRevise()}
            placeholder="Describe what you'd like different..."
            className="flex-1 px-2 py-1 text-sm bg-gray-800 border border-gray-600 rounded text-gray-200 placeholder-gray-500 focus:outline-none focus:border-purple-500"
            autoFocus
          />
          <DomainCardButton
            onClick={handleRevise}
            disabled={!editText.trim()}
            variant="primary"
            className="px-2 py-0.5"
          >
            Revise
          </DomainCardButton>
          <DomainCardButton
            onClick={() => { setEditingKey(null); setEditText(''); }}
            variant="secondary"
            className="px-2 py-0.5"
          >
            ✕
          </DomainCardButton>
        </div>
      )}

      {/* Level-up note */}
      {targetLevel > 1 && (
        <div className="px-4 py-2 border-t border-gray-700">
          <p className="text-xs text-purple-300">
            Creates at level 1, then guides you through level-ups to level {targetLevel}.
          </p>
        </div>
      )}

      {/* Create button */}
      <div className="px-4 py-2 border-t border-gray-700">
        <DomainCardButton
          onClick={onConfirm}
          disabled={!allApproved || anyRevising}
          variant="activeSuccess"
          className="w-full px-4 py-2 text-sm"
        >
          Create Character
        </DomainCardButton>
      </div>
    </div>
  );
}

export default BrewmasterProposalReview;
